/**
 * TASK-TGE-006: Spawn Effect Renderer
 *
 * Digital "rendering in" effect for creature spawns.
 * Features scanline sweep animation and glow burst at completion.
 */

import * as PIXI from 'pixi.js';
import type { GridEffectsConfig } from '../../types/terrain';

/**
 * Render the spawn effect for a creature
 * Creates a scanline sweep from bottom to top with a glow burst at completion
 *
 * @param graphics - PIXI.Graphics instance to draw on
 * @param center - Center point where the creature is spawning
 * @param progress - Animation progress (0-1)
 * @param config - Grid effects configuration
 */
export function renderSpawnEffect(
  graphics: PIXI.Graphics,
  center: { x: number; y: number },
  progress: number,
  config: GridEffectsConfig
): void {
  const { glowColor } = config;

  // Clear previous frame
  graphics.clear();

  // Scanline effect: horizontal line that sweeps upward (active until 80% progress)
  if (progress < 0.8) {
    const scanlineHeight = 64; // Total height to sweep
    const scanlineThickness = 8;

    // Calculate current Y position (moves from bottom to top)
    const scanY = center.y + scanlineHeight / 2 - progress * scanlineHeight;

    // Fade out the scanline as it progresses
    const scanAlpha = 0.6 * (1 - progress / 0.8);

    // Draw scanline rectangle
    graphics.rect(
      center.x - 32,
      scanY,
      64,
      scanlineThickness
    );
    graphics.fill({
      color: glowColor,
      alpha: scanAlpha,
    });
  }

  // Glow burst: expanding circle at the end (starts at 70% progress)
  if (progress > 0.7) {
    const burstProgress = (progress - 0.7) / 0.3; // 0-1 over the last 30%
    const radius = 20 + burstProgress * 30; // Expands from 20 to 50 pixels
    const alpha = 0.5 * (1 - burstProgress); // Fades out as it expands

    // Draw expanding glow circle
    graphics.circle(center.x, center.y, radius);
    graphics.fill({
      color: glowColor,
      alpha,
    });
  }
}
